// oneSignalService.ts - Unified flow with robust error handling
import { OMNISTACK_BASE_URL } from '@/hooks/common/common-functions';
import { CLIENT_X_API_KEY, API_KEY } from '@/config/env';

export class OneSignalService {
  // Detect if we're on iOS PWA
  static isIOSPWA(): boolean {
    return (window.navigator as any).standalone === true || 
           (window.matchMedia('(display-mode: standalone)').matches && /iPad|iPhone|iPod/.test(navigator.userAgent));
  }

  static async handlePostLogin(loginResponse: any) {
    try {
      localStorage.setItem('handlePostLogin_entered', new Date().toISOString());
      localStorage.setItem('handlePostLogin_response', JSON.stringify(loginResponse));
      localStorage.setItem('is_ios_pwa', this.isIOSPWA() ? 'true' : 'false');
      
      const alreadyAsked = localStorage.getItem('notifications_asked');
      localStorage.setItem('notifications_already_asked', alreadyAsked || 'false');
      
      if (alreadyAsked) {
        localStorage.setItem('handlePostLogin_path', 'login_only');
        // Still need to login to OneSignal even if permission was asked before
        try {
          await this.loginToOneSignal(loginResponse);
        } catch (error) {
          console.warn('OneSignal: Login-only failed, but user login continues', error);
        }
        return;
      }

      localStorage.setItem('handlePostLogin_path', 'request_permission');
      
      // UNIFIED FLOW: Same for both desktop and iOS PWA
      setTimeout(() => {
        this.requestPermissionAndRegister(loginResponse);
      }, 3000);
      
    } catch (error) {
      localStorage.setItem('handlePostLogin_error', JSON.stringify({
        // @ts-ignore
        message: error.message,
        // @ts-ignore
        stack: error.stack,
        timestamp: new Date().toISOString()
      }));
      console.error('OneSignal: Post-login setup failed:', error);
      // 🔥 Don't re-throw - let user login continue
    }
  }

  static async requestPermissionAndRegister(loginResponse: any) {
    try {
      localStorage.setItem('requestPermission_entered', new Date().toISOString());
      localStorage.setItem('requestPermission_response', JSON.stringify(loginResponse));
      
      const currentPermission = Notification.permission;
      localStorage.setItem('requestPermission_current_permission', currentPermission);

      if (currentPermission === 'denied') {
        localStorage.setItem('requestPermission_path', 'denied');
        localStorage.setItem('notifications_asked', 'true');
        localStorage.setItem('notifications_status', 'denied');
        // Still login to OneSignal for external ID mapping
        try {
          await this.loginToOneSignal(loginResponse);
        } catch (error) {
          console.warn('OneSignal: Login after denial failed, but user login continues', error);
        }
        return;
      }

      let permissionGranted = false;

      if (currentPermission === 'granted') {
        localStorage.setItem('requestPermission_path', 'already_granted');
        permissionGranted = true;
      } else {
        localStorage.setItem('requestPermission_path', 'requesting');
        
        try {
          localStorage.setItem('requestPermission_calling_wait', new Date().toISOString());
          await this.waitForOneSignal();
          localStorage.setItem('requestPermission_wait_completed', new Date().toISOString());
          
          const OneSignal = (window as any).OneSignal;
          localStorage.setItem('requestPermission_onesignal_exists', !!OneSignal ? 'true' : 'false');
          
          if (this.isIOSPWA()) {
            // iOS PWA: Use native permission request directly
            localStorage.setItem('requestPermission_using_native', 'true');
            const browserPermission = await Notification.requestPermission();
            localStorage.setItem('requestPermission_native_result', browserPermission);
            permissionGranted = browserPermission === 'granted';
          } else {
            // Desktop: Use OneSignal slidedown
            localStorage.setItem('requestPermission_using_slidedown', 'true');
            localStorage.setItem('requestPermission_calling_slidedown', new Date().toISOString());
            await OneSignal.Slidedown.promptPush();
            localStorage.setItem('requestPermission_slidedown_completed', new Date().toISOString());
            
            const newPermission = Notification.permission;
            localStorage.setItem('requestPermission_new_permission', newPermission);
            permissionGranted = newPermission === 'granted';
          }
          
        } catch (error) {
          localStorage.setItem('requestPermission_error', JSON.stringify({
            // @ts-ignore
            message: error.message,
            timestamp: new Date().toISOString()
          }));
          console.warn('OneSignal: Permission request failed, trying native fallback:', error);
          
          try {
            localStorage.setItem('requestPermission_calling_native_fallback', new Date().toISOString());
            const browserPermission = await Notification.requestPermission();
            localStorage.setItem('requestPermission_native_fallback_result', browserPermission);
            permissionGranted = browserPermission === 'granted';
          } catch (nativeError) {
            localStorage.setItem('requestPermission_native_error', JSON.stringify({
              // @ts-ignore
              message: nativeError.message,
              timestamp: new Date().toISOString()
            }));
            console.error('OneSignal: Native prompt also failed:', nativeError);
            permissionGranted = false;
          }
        }
      }
      
      localStorage.setItem('notifications_asked', 'true');
      localStorage.setItem('requestPermission_final_granted', permissionGranted ? 'true' : 'false');
      
      if (permissionGranted) {
        localStorage.setItem('notifications_status', 'enabled');
        localStorage.setItem('requestPermission_calling_login', new Date().toISOString());
        
        // 🔥 IMPROVED: Don't let OneSignal errors break the flow
        try {
          await this.loginToOneSignal(loginResponse);
        } catch (error) {
          console.warn('OneSignal: Login after permission failed, but user login continues', error);
          localStorage.setItem('notifications_status', 'error');
        }
        
      } else {
        localStorage.setItem('notifications_status', 'denied');
        localStorage.setItem('requestPermission_calling_login_denied', new Date().toISOString());
        
        // Still try to login to OneSignal for future use
        try {
          await this.loginToOneSignal(loginResponse);
        } catch (error) {
          console.warn('OneSignal: Login after denial failed, but user login continues', error);
        }
      }
    } catch (error) {
      localStorage.setItem('requestPermission_error', JSON.stringify({
        // @ts-ignore
        message: error.message,
        // @ts-ignore
        stack: error.stack,
        timestamp: new Date().toISOString()
      }));
      console.error('OneSignal: Permission request failed:', error);
      localStorage.setItem('notifications_asked', 'true');
      localStorage.setItem('notifications_status', 'error');
      
      // 🔥 CRITICAL: Don't re-throw here either
    }
  }

  static async loginToOneSignal(loginResponse: any) {
    try {
      localStorage.setItem('loginToOneSignal_entered', new Date().toISOString());
      localStorage.setItem('loginToOneSignal_response', JSON.stringify(loginResponse));
      
      // Track that we started
      localStorage.setItem('onesignal_login_started', new Date().toISOString());
      
      localStorage.setItem('loginToOneSignal_calling_wait', new Date().toISOString());
      await this.waitForOneSignal();
      localStorage.setItem('onesignal_waited', 'true');
      localStorage.setItem('loginToOneSignal_wait_completed', new Date().toISOString());
      
      const OneSignal = (window as any).OneSignal;
      localStorage.setItem('onesignal_object_exists', !!OneSignal ? 'true' : 'false');
      localStorage.setItem('loginToOneSignal_onesignal_check', !!OneSignal ? 'exists' : 'missing');
      
      const businessId = loginResponse.businessId || loginResponse.auth_response?.businessId;
      const userId = loginResponse.userId || loginResponse.auth_response?.userId;
      
      localStorage.setItem('onesignal_ids', JSON.stringify({ businessId, userId }));
      localStorage.setItem('loginToOneSignal_extracted_ids', JSON.stringify({ businessId, userId }));
      
      const externalUserId = `${businessId}_${userId}`;
      localStorage.setItem('onesignal_external_id', externalUserId);
      localStorage.setItem('loginToOneSignal_external_id', externalUserId);
      
      // 🔥 IMPROVED: Add robust error handling around OneSignal.login()
      localStorage.setItem('loginToOneSignal_calling_login', new Date().toISOString());
      try {
        await OneSignal.login(externalUserId);
        localStorage.setItem('onesignal_login_called', 'true');
        localStorage.setItem('loginToOneSignal_login_completed', new Date().toISOString());
      } catch (loginError) {
        // Handle OneSignal SDK login errors gracefully
        localStorage.setItem('loginToOneSignal_login_error', JSON.stringify({
          // @ts-ignore
          message: loginError.message,
          // @ts-ignore
          name: loginError.name,
          timestamp: new Date().toISOString()
        }));
        
        console.warn('OneSignal: SDK login failed, but continuing...', loginError);
        
        // Don't throw - continue with the flow
        localStorage.setItem('onesignal_login_called', 'false');
        localStorage.setItem('loginToOneSignal_login_completed', new Date().toISOString());
      }
      
      // Wait time: 3 seconds for both desktop and iOS PWA
      const waitTime = 3000;
      
      localStorage.setItem('loginToOneSignal_starting_verification', new Date().toISOString());
      localStorage.setItem('loginToOneSignal_wait_time_used', waitTime.toString());
      
      setTimeout(async () => {
        try {
          localStorage.setItem('loginToOneSignal_verification_timeout_reached', new Date().toISOString());
          
          let retries = 0;
          const maxRetries = 5;
          const retryDelay = 1000;
          
          localStorage.setItem('loginToOneSignal_max_retries', maxRetries.toString());
          localStorage.setItem('loginToOneSignal_retry_delay', retryDelay.toString());
          
          while (retries < maxRetries) {
            try {
              localStorage.setItem('loginToOneSignal_verification_attempt', retries.toString());
              const verifiedExternalId = await OneSignal.User.externalUserId;
              localStorage.setItem('loginToOneSignal_verified_id', verifiedExternalId || 'null');
              
              if (verifiedExternalId === externalUserId) {
                localStorage.setItem('loginToOneSignal_verification_success', new Date().toISOString());
                
                // Now register with backend
                localStorage.setItem('loginToOneSignal_calling_getdata', new Date().toISOString());
                const oneSignalData = await this.getOneSignalData();
                localStorage.setItem('loginToOneSignal_getdata_result', JSON.stringify(oneSignalData));
                
                if (oneSignalData) {
                  localStorage.setItem('loginToOneSignal_calling_register', new Date().toISOString());
                  await this.registerWithBackend(loginResponse, oneSignalData);
                  localStorage.setItem('loginToOneSignal_register_completed', new Date().toISOString());
                }
                return;
              }
              
              retries++;
              if (retries < maxRetries) {
                localStorage.setItem('loginToOneSignal_waiting_retry', `${retries}/${maxRetries}`);
                await new Promise(resolve => setTimeout(resolve, retryDelay));
              }
            } catch (error) {
              localStorage.setItem('loginToOneSignal_verification_error', JSON.stringify({
                retry: retries,
                // @ts-ignore
                error: error.message,
                timestamp: new Date().toISOString()
              }));
              console.warn('OneSignal: Verification failed, retrying...', error);
              retries++;
              if (retries < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, retryDelay));
              }
            }
          }
          
          localStorage.setItem('loginToOneSignal_verification_failed', new Date().toISOString());
          console.warn('OneSignal: Could not verify external ID in frontend, but continuing...');
          
          // Still try to register with backend
          localStorage.setItem('loginToOneSignal_calling_getdata', new Date().toISOString());
          const oneSignalData = await this.getOneSignalData();
          localStorage.setItem('loginToOneSignal_getdata_result', JSON.stringify(oneSignalData));
          
          if (oneSignalData) {
            localStorage.setItem('loginToOneSignal_calling_register', new Date().toISOString());
            await this.registerWithBackend(loginResponse, oneSignalData);
            localStorage.setItem('loginToOneSignal_register_completed', new Date().toISOString());
          }
        } catch (timeoutError) {
          localStorage.setItem('loginToOneSignal_timeout_error', JSON.stringify({
            // @ts-ignore
            message: timeoutError.message,
            timestamp: new Date().toISOString()
          }));
          console.warn('OneSignal: Timeout error in verification, but continuing...', timeoutError);
        }
      }, waitTime);
      
    } catch (error) {
      localStorage.setItem('loginToOneSignal_error', JSON.stringify({
        // @ts-ignore
        message: error.message,
        // @ts-ignore
        stack: error.stack,
        // @ts-ignore
        timestamp: new Date().toISOString()
      }));
      console.error('OneSignal: Login failed:', error);
      
      // 🔥 CRITICAL: Don't re-throw - just log and continue
      // This prevents breaking the entire login flow
    }
  }

  static async waitForOneSignal(): Promise<void> {
    localStorage.setItem('waitForOneSignal_entered', new Date().toISOString());
    
    const timeout = 15000;
    const startTime = Date.now();
    let attempts = 0;
    
    while (Date.now() - startTime < timeout) {
      attempts++;
      localStorage.setItem('waitForOneSignal_attempts', attempts.toString());
      
      try {
        const OneSignal = (window as any).OneSignal;
        localStorage.setItem('waitForOneSignal_check_result', JSON.stringify({
          OneSignal: !!OneSignal,
          User: !!OneSignal?.User,
          Slidedown: !!OneSignal?.Slidedown,
          login: !!OneSignal?.login,
          attempt: attempts
        }));
        
        if (OneSignal && OneSignal.User && OneSignal.login) {
          // For iOS PWA, we don't need Slidedown - just User and login
          if (this.isIOSPWA()) {
            localStorage.setItem('waitForOneSignal_success', new Date().toISOString());
            return;
          }
          // For desktop, we need Slidedown too
          else if (OneSignal.Slidedown) {
            localStorage.setItem('waitForOneSignal_success', new Date().toISOString());
            return;
          }
        }
      } catch (error) {
        localStorage.setItem('waitForOneSignal_check_error', JSON.stringify({
          // @ts-ignore
          message: error.message,
          attempt: attempts,
          timestamp: new Date().toISOString()
        }));
      }
      
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    localStorage.setItem('waitForOneSignal_timeout', new Date().toISOString());
    throw new Error('OneSignal SDK not ready after timeout');
  }

  static async getOneSignalData(): Promise<{oneSignalId: string, subscriptionId: string | null, externalUserId: string | null} | null> {
    try {
      localStorage.setItem('getOneSignalData_entered', new Date().toISOString());
      
      const timeout = 20000;
      const retryDelay = 1000;
      
      localStorage.setItem('getOneSignalData_timeout_used', timeout.toString());
      localStorage.setItem('getOneSignalData_retry_delay_used', retryDelay.toString());
      
      const startTime = Date.now();
      let attempts = 0;
      
      while (Date.now() - startTime < timeout) {
        attempts++;
        localStorage.setItem('getOneSignalData_attempts', attempts.toString());
        
        try {
          const OneSignal = (window as any).OneSignal;
          
          if (OneSignal && OneSignal.User) {
            localStorage.setItem('getOneSignalData_onesignal_ready', new Date().toISOString());
            
            const oneSignalId = await OneSignal.User.onesignalId;
            localStorage.setItem('getOneSignalData_onesignal_id', oneSignalId || 'null');
            
            if (oneSignalId) {
              let subscriptionId = null;
              let externalUserId = null;
              
              try {
                const isOptedIn = await OneSignal.User.PushSubscription.optedIn;
                localStorage.setItem('getOneSignalData_opted_in', isOptedIn ? 'true' : 'false');
                
                if (isOptedIn) {
                  subscriptionId = await OneSignal.User.PushSubscription.id;
                  localStorage.setItem('getOneSignalData_subscription_id', subscriptionId || 'null');
                }
                
                try {
                  externalUserId = await OneSignal.User.externalUserId;
                  localStorage.setItem('getOneSignalData_external_id_method1', externalUserId || 'null');
                  
                  if (!externalUserId) {
                    const identity = await OneSignal.User.identity;
                    externalUserId = identity?.external_id || identity?.externalUserId;
                    localStorage.setItem('getOneSignalData_external_id_method2', externalUserId || 'null');
                  }
                } catch (extError) {
                  localStorage.setItem('getOneSignalData_external_id_error', JSON.stringify({
                    // @ts-ignore
                    message: extError.message,
                    timestamp: new Date().toISOString()
                  }));
                }
                
              } catch (subError) {
                localStorage.setItem('getOneSignalData_subscription_error', JSON.stringify({
                  // @ts-ignore
                  message: subError.message,
                  timestamp: new Date().toISOString()
                }));
              }
              
              const result = {
                oneSignalId,
                subscriptionId,
                externalUserId
              };
              
              localStorage.setItem('getOneSignalData_success', JSON.stringify(result));
              return result;
            }
          }
        } catch (error) {
          localStorage.setItem('getOneSignalData_loop_error', JSON.stringify({
            // @ts-ignore
            message: error.message,
            attempt: attempts,
            timestamp: new Date().toISOString()
          }));
        }
        
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
      
      localStorage.setItem('getOneSignalData_timeout', new Date().toISOString());
      return null;
    } catch (error) {
      localStorage.setItem('getOneSignalData_error', JSON.stringify({
        // @ts-ignore
        message: error.message,
        // @ts-ignore
        stack: error.stack,
        timestamp: new Date().toISOString()
      }));
      return null;
    }
  }

  static async registerWithBackend(loginResponse: any, oneSignalData: {oneSignalId: string, subscriptionId: string | null, externalUserId: string | null}) {
    try {
      localStorage.setItem('registerWithBackend_entered', new Date().toISOString());
      localStorage.setItem('registerWithBackend_data', JSON.stringify({ loginResponse, oneSignalData }));
      
      const businessApiKey = localStorage.getItem('businessApiKey');
      
      const originalRole = loginResponse.account_type || loginResponse.auth_response?.account_type || 'business_staff';
      const mappedRole = originalRole === 'business' ? 'business_admin' : originalRole;
   
      const getPlatform = () => {
        const isIOSPWA = (window.navigator as any).standalone === true;
        const isAndroidPWA = window.matchMedia('(display-mode: standalone)').matches && /Android/i.test(navigator.userAgent);
        const isPWA = isIOSPWA || isAndroidPWA;
        
        if (isPWA) {
          return 'pwa';
        }
        return 'web';
      };

      const requestData = {
        businessId: loginResponse.businessId || loginResponse.auth_response?.businessId,
        userId: loginResponse.userId || loginResponse.auth_response?.userId,
        playerId: oneSignalData.oneSignalId, // Use actual OneSignal ID
        platform: getPlatform(), // 'web' | 'pwa'
        userRole: mappedRole,
        subscriptionId: oneSignalData.subscriptionId,
        externalUserId: oneSignalData.externalUserId,
      };
   
      const timestamp = new Date().toISOString();
      localStorage.setItem('onesignal_last_attempt', timestamp);
      localStorage.setItem('onesignal_status', 'registering');
      localStorage.setItem('registerWithBackend_request_data', JSON.stringify(requestData));
   
      localStorage.setItem('registerWithBackend_calling_fetch', new Date().toISOString());
      const response = await fetch(
        `${OMNISTACK_BASE_URL}auth/register-notifications`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'business-x-api-key': businessApiKey ?? '',
            'client-x-api-key': CLIENT_X_API_KEY,
            'x-api-key': API_KEY
          },
          body: JSON.stringify(requestData),
        }
      );
   
      localStorage.setItem('registerWithBackend_fetch_completed', new Date().toISOString());
      const result = await response.json();
      localStorage.setItem('registerWithBackend_response_parsed', new Date().toISOString());
   
      localStorage.setItem('onesignal_last_result', JSON.stringify({
        timestamp,
        success: response.ok,
        status: response.status,
        requestData,
        response: result
      }));
   
      localStorage.setItem('onesignal_status', response.ok ? 'success' : 'failed');
   
      if (!response.ok) {
        localStorage.setItem('registerWithBackend_not_ok', JSON.stringify(result));
        throw new Error(result.message || 'Failed to register notifications');
      }
   
      if (result?.success) {
        localStorage.setItem('registerWithBackend_success', new Date().toISOString());
        localStorage.setItem('notifications_registered', 'true');
        localStorage.setItem('oneSignalId', oneSignalData.oneSignalId);
        
        if (oneSignalData.externalUserId) {
          localStorage.setItem('externalUserId', oneSignalData.externalUserId);
        }
      }
    } catch (error) {
      localStorage.setItem('registerWithBackend_error', JSON.stringify({
        // @ts-ignore
        message: error.message,
        // @ts-ignore
        stack: error.stack,
        timestamp: new Date().toISOString()
      }));
      
      localStorage.setItem('onesignal_last_result', JSON.stringify({
        timestamp: new Date().toISOString(),
        success: false,
        // @ts-ignore
        error: error.message
      }));
      
      localStorage.setItem('onesignal_status', 'error');
      
      // 🔥 Don't re-throw - let the process continue
    }
  }

  static reset() {
    localStorage.removeItem('notifications_asked');
    localStorage.removeItem('notifications_status');
    localStorage.removeItem('notifications_registered');
    localStorage.removeItem('notifications_asked');
    localStorage.removeItem('oneSignalId');
    localStorage.removeItem('externalUserId');
  }

  static getStatus() {
    return {
      asked: localStorage.getItem('notifications_asked') === 'true',
      status: localStorage.getItem('notifications_status'),
      registered: localStorage.getItem('notifications_registered') === 'true',
      oneSignalId: localStorage.getItem('oneSignalId'),
      externalUserId: localStorage.getItem('externalUserId')
    };
  }
}